import { Breadcrumb } from "components/atoms/Breadcrumb";
import { FortuneTellerList } from "components/templates/FortuneTellerList";
import useFortuneTellerListContext from "features/fortune-tellers/hooks/useFortuneTellerListContext";
import { useBreadcrumb } from "features/shared/hooks/useBreadcrumb";
import { GetServerSideProps } from "next";
import { NextSeo, NextSeoProps } from "next-seo";
import { useRouter } from "next/router";
import { WaitingStatus } from "repositories/FortuneTellerRepository";
import { useFortuneTellerList } from "repositories/hooks";
import { FortuneTellerOrderBy, MenuTypeV2 } from "shared/types";
import { Paths } from "utils/routes";
import { MetaTag } from "utils/seo";
import { getAroundPageUrl } from "utils/ssr/getAroundPageUrl";
import getNextSeoProps from "utils/ssr/getNextSeoProps";

interface Props {
  menuType: MenuTypeV2;
  waitingStatus?: WaitingStatus;
  orderBy?: FortuneTellerOrderBy;
  shuffle?: boolean;
  nextSeoProps: NextSeoProps;
}

const FortuneTellersIndex = ({
  menuType,
  waitingStatus,
  orderBy,
  shuffle,
  nextSeoProps,
}: Props) => {
  const { query } = useRouter();
  const page = Number(query.page ?? 1);

  const { reqParams, title, description, perPage } =
    useFortuneTellerListContext();

  const { fortuneTellers, meta, isLoading } = useFortuneTellerList(reqParams);
  const breadCrumbItems = useBreadcrumb([
    { name: "占い師一覧", item: Paths.fortuneTellers.index() },
    {
      name: title,
      item: `${Paths.fortuneTellers.index()}?menuType=${menuType}&waitingStatus=${waitingStatus}&orderBy=${orderBy}&shuffle=${shuffle}`,
    },
  ]);
  return (
    <>
      <Breadcrumb itemListElements={breadCrumbItems} />
      <NextSeo {...nextSeoProps} />
      <FortuneTellerList
        title={page > 1 ? `${title}(${page}ページ目)` : title}
        description={description}
        fortuneTellers={fortuneTellers}
        page={page}
        perPage={perPage}
        totalPages={meta?.totalPages}
        withRank={orderBy === "repeat_rate"}
        loading={isLoading}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { query } = ctx;

  const { title, description, keywords } = (() => {
    if (query.menuType === "chat" && query.orderBy === "repeat_rate") {
      return { ...MetaTag.recommendedFortuneTellersList };
    } else if (query.menuType === "tel" && query.orderBy === "repeat_rate") {
      return { ...MetaTag.recommendedTelFortuneTellersList };
    } else if (query.menuType === "chat" && query.waitingStatus === "waiting") {
      return { ...MetaTag.chatWaitingFortuneTellersList };
    } else if (query.menuType === "tel" && query.waitingStatus === "waiting") {
      return { ...MetaTag.telWaitingFortuneTellersList };
    } else {
      return { ...MetaTag.newFortuneTellersList };
    }
  })();

  const { prevUrl, nextUrl } = getAroundPageUrl(ctx);

  const page = Number(query.page ?? 1);

  const nextSeoProps = getNextSeoProps(ctx, {
    title: page > 1 ? `(${query.page}ページ目)${title}` : title,
    description,
    keywords,
    options: {
      additionalLinkTags: [
        prevUrl
          ? {
              rel: "prev",
              href: prevUrl,
            }
          : null,
        {
          rel: "next",
          href: nextUrl,
        },
      ].filter(Boolean),
    },
  });

  return {
    props: {
      menuType: query.menuType ?? "",
      waitingStatus: query.waitingStatus ?? "",
      orderBy: query.orderBy ?? "",
      shuffle: query.shuffle ?? "",
      nextSeoProps: nextSeoProps,
    },
  };
};

export default FortuneTellersIndex;
